import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { USEREDIT } from '../../../routes/paths';
import { useSelector } from 'react-redux';
import { Chip, Tooltip, IconButton, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import Person from '@mui/icons-material/Person';
import Swal from 'sweetalert2';
import { RootState } from '../../../redux/store';
import { useAuthContext } from '../../../contexts/authContext';
import { SwalCustomClass } from '../../components/general/toastSwals/swalCustomClass';
import { UsersMessages } from '../../../shared/messages/usersMessages';
import {
    asyncDeleteUser,
    asyncGetUsers,
    asyncReactivateUser,
} from '../../../services/users.service';
import variables from '../../../shared/styles/settings.module.scss';
import { User, UserFilter } from '../../../shared/types/user';
import { Table } from '../../components/general/table';
import { GridColDef } from '@mui/x-data-grid';

export interface UserListProps {
    filters?: UserFilter;
}

const UserList = ({ filters }: UserListProps) => {
    const navigate = useNavigate();
    const { modeTheme } = useAuthContext();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { id: idUserLog, type } = logUser || {};
    const [rows, setRows] = useState<any>();
    const [lengthRows, setLengthRows] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(20);
    const { usersMessages }: any = UsersMessages;
    const [finalFilters, setFinalFilters] = useState<UserFilter>({});

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        asyncGetUsers(finalFilters, page, pageSize)
            .then((body: any) => {
                if (body.items !== undefined) {
                    setRows(body.items.items);
                    setLengthRows(body.items.length);
                } else if (body.message !== undefined) {
                    SwalCustomClass.fire({
                        text: usersMessages[body.message],
                        icon: 'error',
                    });
                }
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters, usersMessages]);

    const deleteOrActiveUser = (row: any) => {
        Swal.fire({
            title: row.row.active
                ? usersMessages.TITLE_INACTIVE
                : usersMessages.TITLE_REACTIVATE,
            text: row.row.active
                ? usersMessages.MESSAGE_INACTIVE
                : usersMessages.MESSAGE_REACTIVATE,
            icon: 'question',
            iconColor:
                modeTheme === 'darkMode'
                    ? variables.darkColor
                    : variables.primaryColor,
            showDenyButton: true,
            denyButtonText: 'NO',
            denyButtonColor:
                modeTheme === 'darkMode'
                    ? variables.darkColor
                    : variables.primaryColor,
            confirmButtonText: 'SI',
            confirmButtonColor:
                modeTheme === 'darkMode'
                    ? variables.darkColor
                    : variables.primaryColor,
            customClass: 'swal-wide-class',
        }).then((response) => {
            if (response.isConfirmed) {
                (row.row.active ? asyncDeleteUser : asyncReactivateUser)(
                    row.id
                ).then((body: any) => {
                    if (body.items !== undefined) {
                        SwalCustomClass.fire({
                            text: row.row.active
                                ? usersMessages.MESSAGE_INACTIVE_OK
                                : usersMessages.MESSAGE_REACTIVATE_OK,
                            icon: 'success',
                            iconColor:
                                modeTheme === 'darkMode'
                                    ? variables.darkColor
                                    : variables.primaryColor,
                        });
                        asyncGetUsers({}, page, pageSize).then((body: any) => {
                            if (body.items !== undefined) {
                                setRows(body.items.items);
                                setLengthRows(body.items.length);
                            }
                        });
                    } else {
                        SwalCustomClass.fire({
                            text:
                                usersMessages[body.message] ||
                                'Se presento un inconveniente en este momento, por favor inteta de nuevo mas tarde.',
                            icon: 'error',
                            iconColor:
                                modeTheme === 'darkMode'
                                    ? variables.darkColor
                                    : variables.primaryColor,
                        });
                    }
                });
            }
        });
    };

    const paginator = (paginationModel: any) => {
        if (paginationModel.page !== page) {
            setPage(paginationModel.page);
        } else if (paginationModel.pageSize !== pageSize) {
            setPageSize(paginationModel.pageSize);
        }
    };

    const columns: GridColDef<User>[] = [
        {
            field: 'document',
            headerName: 'Documento',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'surname',
            headerName: 'Apellido',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'username',
            headerName: 'Correo/Nombre de usuario',
            minWidth: 150,
            flex: 0.8,
        },
        {
            field: 'type',
            headerName: 'Tipo de login',
            minWidth: 120,
            flex: 0.5,
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 100,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            renderCell: (row: any) => {
                if (row.row.active === true) {
                    return (
                        <Chip
                            label="ACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="success"
                        />
                    );
                } else {
                    return (
                        <Chip
                            label="INACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="error"
                        />
                    );
                }
            },
        },
    ];

    if (type === 'admin') {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => (
                <Grid textAlign="left" width="100%">
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => navigate(`${USEREDIT}/${row.id}`)}
                        >
                            <EditIcon
                                type="submit"
                                sx={{ color: 'primary.main' }}
                            />
                        </IconButton>
                    </Tooltip>
                    {idUserLog !== row.id && (
                        <Tooltip
                            title={row.row.active ? 'Deshabilitar' : 'Activar'}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => deleteOrActiveUser(row)}
                                >
                                    {row.row.active ? (
                                        <PersonOffIcon
                                            type="submit"
                                            sx={{ color: 'primary.main' }}
                                        />
                                    ) : (
                                        <Person
                                            type="submit"
                                            sx={{ color: 'primary.main' }}
                                        />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Grid>
            ),
        });
    }

    return (
        <Table
            columns={columns}
            rows={rows}
            rowCount={lengthRows}
            loading={loading}
            paginationModel={{
                page,
                pageSize,
            }}
            onPaginationModelChange={paginator}
            pageSizeOptions={[2, 10, 20, 50]}
        />
    );
};

export default UserList;
